export type LanguageContent = { en: string; fr: string };

export type UseCaseResult = {
  label: LanguageContent;
  value: string;
};

export type UseCase = {
  slug: string;
  company: string;
  logo: string;
  /** false = skip brightness(0)invert(1) filter (coloured logos) */
  logoFilter?: boolean;
  sector: LanguageContent;
  sectorColor: string;
  title: LanguageContent;
  description: LanguageContent;
  context: LanguageContent;
  technologies: string[];
  highlights: LanguageContent[];
  results: UseCaseResult[];
  duration: LanguageContent;
  ongoing?: boolean;
};

export const USE_CASES: UseCase[] = [
  {
    slug: 'totalenergies',
    company: 'TotalEnergies',
    logo: '/images/projects/TotalEnergies.png',
    sector: { fr: 'Énergie & Industrie', en: 'Energy & Industry' },
    sectorColor: '#F97316',
    title: {
      fr: "Plateforme IoT de gestion d'un parc de 130 000 capteurs",
      en: 'IoT Fleet Management Platform — 130,000 Industrial Sensors',
    },
    description: {
      fr: "Pendant 5 ans, Blue Owl SAS a conçu et développé la plateforme centrale de supervision IoT de TotalEnergies, garantissant la collecte, le traitement et la supervision de millions de messages quotidiens issus d'un parc de 130 000 capteurs LP-WAN déployés sur des installations industrielles mondiales.",
      en: "Over 5 years, Blue Owl SAS designed and developed TotalEnergies\u2019 central IoT supervision platform, guaranteeing the collection, processing and supervision of millions of daily messages from a fleet of 130,000 LP-WAN sensors deployed on industrial sites worldwide.",
    },
    context: {
      fr: "TotalEnergies déployait 130 000 capteurs sur ses installations sans outil centralisé. Les opérateurs lançaient manuellement des scripts de scan chaque matin — 3 heures de travail non-automatisées, avec des risques d'oubli et aucune garantie de récupération complète des messages sur réseaux LP-WAN à faible débit.",
      en: 'TotalEnergies deployed 130,000 sensors across facilities with no centralized management tool. Operators ran manual scan scripts every morning — 3 hours of non-automated work, with risks of missed devices and no guarantee of complete message recovery over low-throughput LP-WAN networks.',
    },
    technologies: ['IoT', 'LP-WAN', 'Sigfox', 'AWS', 'Azure', 'Python', 'Go', 'JavaScript'],
    highlights: [
      { fr: 'Intégration agnostique multi-protocoles (Sigfox, LP-WAN)', en: 'Multi-protocol agnostic integration (Sigfox, LP-WAN)' },
      { fr: "Gestion automatisée des zones blanches réseau", en: 'Automated management of network coverage gaps' },
      { fr: "Mécanisme de dépilage garantissant la récupération complète de l'historique des messages", en: 'Message backfill mechanism ensuring full history recovery' },
      { fr: 'Configuration cloud-to-device unitaire à distance sur 130 000 appareils', en: 'Remote per-device cloud-to-device configuration across 130,000 devices' },
      { fr: 'Estimation prédictive de la durée de vie des batteries (1 à 6 ans)', en: 'Predictive battery lifetime estimation (1–6 years)' },
      { fr: "Ouverture de la plateforme à d'autres familles de capteurs (extension Stedis)", en: 'Platform extended to additional sensor families (Stedis rollout)' },
    ],
    results: [
      { label: { fr: 'Récupération des messages', en: 'Message recovery' }, value: '>95%' },
      { label: { fr: 'Scan manuel quotidien', en: 'Daily manual scan' }, value: '3h → 0' },
      { label: { fr: 'Capteurs supervisés', en: 'Managed sensors' }, value: '130 000' },
      { label: { fr: 'Durée du partenariat', en: 'Partnership' }, value: '5 ans' },
    ],
    duration: { fr: '5 ans de partenariat', en: '5-year partnership' },
  },

  {
    slug: 'hydroquebec',
    company: 'Hydro-Québec',
    logo: '/images/projects/Hydro-Québec_logo.svg',
    sector: { fr: 'Énergie & Recherche', en: 'Energy & Research' },
    sectorColor: '#00BCD4',
    title: {
      fr: 'Capteur IoT autonome sur lignes haute tension — from scratch',
      en: 'Autonomous IoT Sensor on High-Voltage Lines — Built from Scratch',
    },
    description: {
      fr: "Pour l'IREQ (Institut de Recherche d'Hydro-Québec), Blue Owl SAS a développé de zéro un capteur IoT autonome en énergie, fixé sur des conducteurs haute tension. Du firmware embarqué au pipeline cloud Azure industrialisable, en passant par la double redondance réseau cellulaire et satellite, nous livrons une solution inédite sur le marché.",
      en: 'For IREQ (Hydro-Québec Research Institute), Blue Owl SAS built from scratch an energy-autonomous IoT sensor mounted on high-voltage conductors. From embedded firmware to an industrializable Azure cloud pipeline, including dual cellular and satellite network redundancy, we are delivering a unique solution.',
    },
    context: {
      fr: "L'IREQ souhaitait prototyper un capteur capable de collecter ~50 paramètres électriques sur des lignes haute tension, sans batterie ni alimentation externe. Les données devaient être transmises toutes les 5 minutes vers une infrastructure cloud analytique robuste, avec redondance réseau en cas de défaillance cellulaire.",
      en: 'IREQ needed to prototype a sensor capable of collecting ~50 electrical parameters on high-voltage lines, with no battery or external power source. Data had to be transmitted every 5 minutes to a robust analytics cloud infrastructure, with network redundancy in case of cellular failure.',
    },
    technologies: ['nRF9151', 'FPGA', 'MQTT', 'Azure IoT Hub', 'Event Hub', 'Azure Functions', 'ADX', 'LTE-M', 'Satellite'],
    highlights: [
      { fr: 'Firmware embarqué nRF9151 from scratch — 50 mesures distinctes avec pré-traitement FPGA', en: 'nRF9151 embedded firmware from scratch — 50 distinct measurements with FPGA pre-processing' },
      { fr: 'Capteur 100% autonome en énergie (harvesting via champ magnétique du conducteur)', en: '100% energy-autonomous sensor (harvesting from conductor magnetic field)' },
      { fr: 'Double redondance réseau : LTE-M cellulaire + satellite', en: 'Dual network redundancy: cellular LTE-M + satellite' },
      { fr: 'Pipeline Azure temps réel : IoT Hub → Event Hub → Azure Functions → ADX', en: 'Real-time Azure pipeline: IoT Hub → Event Hub → Azure Functions → ADX' },
      { fr: "Architecture cloud industrialisable en cours de déploiement aux standards de l'entreprise", en: 'Industrializable cloud architecture being deployed to enterprise standards' },
    ],
    results: [
      { label: { fr: 'Paramètres mesurés', en: 'Measured parameters' }, value: '50+' },
      { label: { fr: 'Fréquence de transmission', en: 'Transmission frequency' }, value: '5 min' },
      { label: { fr: 'Redondances réseau', en: 'Network paths' }, value: 'Cell. + Sat.' },
      { label: { fr: 'Alimentation requise', en: 'Power supply' }, value: '0 W ext.' },
    ],
    duration: { fr: '2 ans — en cours', en: '2 years — ongoing' },
    ongoing: true,
  },

  {
    slug: 'fraidom',
    company: 'FrAIdom',
    logo: '/images/projects/FrAIdom.svg',
    logoFilter: false,
    sector: { fr: 'Intelligence Artificielle & SaaS', en: 'Artificial Intelligence & SaaS' },
    sectorColor: '#A78BFA',
    title: {
      fr: 'Assistant IA multi-agents pour freelances — productivité & automatisation',
      en: 'Multi-Agent AI Assistant for Freelancers — Productivity & Automation',
    },
    description: {
      fr: "FrAIdom est un SaaS conçu et développé en interne par Blue Owl SAS pour répondre aux défis quotidiens des freelances : jongler entre clients, automatiser les tâches répétitives et garder une vision claire de son activité — le tout piloté par des agents IA autonomes sur web et mobile.",
      en: "FrAIdom is a SaaS designed and built in-house at Blue Owl SAS to address freelancers\u2019 daily challenges: juggling multiple clients, automating repetitive tasks, and maintaining a clear view of their activity \u2014 all driven by autonomous AI agents across web and mobile.",
    },
    context: {
      fr: "Les freelances manquent d'outils conçus pour leur mode de travail fragmenté : visibilité limitée sur leur activité, temps perdu en gestion administrative, difficulté à scaler sans aide. Les outils généralistes du marché ignorent la réalité du multi-client et de l'hyperspécialisation.",
      en: "Freelancers lack tools designed for their fragmented work reality: limited visibility on their activity, time wasted on administrative tasks, difficulty scaling without support. Generic market tools ignore the multi-client and hyper-specialization reality.",
    },
    technologies: ['React', 'React Native', 'TypeScript', 'Node.js', 'PostgreSQL', 'AI Agents', 'Azure'],
    highlights: [
      { fr: 'Architecture multi-agents IA avec orchestration autonome des tâches', en: 'Multi-agent AI architecture with autonomous task orchestration' },
      { fr: 'Interface cross-plateforme : web React + mobile React Native', en: 'Cross-platform interface: React web + React Native mobile' },
      { fr: "Gestion intelligente des tâches, priorités et charge de travail", en: 'Intelligent task, priority and workload management' },
      { fr: "Tableaux de bord d'activité personnalisés et insights freelance", en: 'Personalised activity dashboards and freelance insights' },
      { fr: 'Backend API robuste TypeScript + Azure — conçu pour la scalabilité', en: 'Robust TypeScript + Azure API backend — designed for scale' },
    ],
    results: [
      { label: { fr: 'Plateformes', en: 'Platforms' }, value: 'Web + Mobile' },
      { label: { fr: 'Agents IA', en: 'AI Agents' }, value: 'Multi-agents' },
      { label: { fr: 'Stade', en: 'Stage' }, value: 'Bêta' },
      { label: { fr: 'Conception', en: 'Origin' }, value: 'In-house' },
    ],
    duration: { fr: 'En développement actif', en: 'In active development' },
    ongoing: true,
  },
];
